import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { X, CheckCircle2 } from "lucide-react";
import type { AuctionSnapshotEvent } from "../../lib/auction-events";
import { usePlatformAuth } from "../../lib/use-platform-auth";
import { formatCurrency } from "../../lib/auction-utils";
import {
  AuctionCardWrapper,
  AuctionCardImage,
  AuctionCardHeader,
  FavoriteButton,
  FlashBadge,
  LiveTimingBadge,
  EndedTimingBadge,
  vehicleDetailsString,
} from "./AuctionCardShared";

type Props = {
  auction: any;
  organizationSlug: string;
  isFavorite?: boolean;
  onToggleFavorite?: (id: string) => void;
  onBidPlaced?: (event: AuctionSnapshotEvent) => void;
};

export function BidAuctionCard({
  auction,
  organizationSlug,
  isFavorite = false,
  onToggleFavorite,
  onBidPlaced,
}: Props) {
  const navigate = useNavigate();
  const { organizations } = usePlatformAuth();
  const [submitting, setSubmitting] = useState(false);
  const [bidError, setBidError] = useState<string | null>(null);

  const currentOrg = organizations.find((o) => o.slug === organizationSlug);
  const isSeller = currentOrg?.id === auction.sellerOrganizationId;
  const isWinning = currentOrg?.id === auction.highestBidderOrganizationId && auction.currentBid > 0;
  const isOutbid = !isWinning && auction.myHighestBid && auction.myHighestBid > 0;

  const prevBidRef = useRef(auction.currentBid);
  const [highlightBid, setHighlightBid] = useState(false);

  useEffect(() => {
    if (auction.currentBid > prevBidRef.current) {
      setHighlightBid(true);
      const timer = setTimeout(() => setHighlightBid(false), 1000);
      prevBidRef.current = auction.currentBid;
      return () => clearTimeout(timer);
    }
    prevBidRef.current = auction.currentBid;
  }, [auction.currentBid]);

  const v = auction.vehicle;
  const details = vehicleDetailsString(v);
  const currentDisplayedBid = auction.bidCount > 0 ? auction.currentBid : auction.startingBid;

  async function placeBid(increment: number) {
    const amount = currentDisplayedBid + increment;
    setSubmitting(true);
    setBidError(null);
    try {
      const api = import.meta.env.VITE_API_URL || "http://localhost:9000";
      const res = await fetch(`${api}/api/auctions/${auction.id}/bids`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount, organizationSlug }),
      });
      const data = await res.json();
      if (!res.ok) {
        setBidError(data.error ?? "Budet ble ikke godtatt");
      } else {
        onBidPlaced?.(data.event as AuctionSnapshotEvent);
      }
    } catch {
      setBidError("Noe gikk galt");
    } finally {
      setSubmitting(false);
    }
  }

  // Status badge
  const statusBadge = (() => {
    if (["pending_seller_decision", "negotiating"].includes(auction.status) && !isSeller) {
      return (
        <div className="flex items-center gap-1.5 rounded-full bg-[#44a044] backdrop-blur px-3 py-1.5 text-[11px] font-black uppercase tracking-wider text-white shadow-lg shadow-green-900/20 ring-2 ring-white/20 animate-in zoom-in-50 duration-300">
          <CheckCircle2 className="h-3.5 w-3.5" />
          <span>{auction.status === "negotiating" ? "Forhandling" : "Avventer svar"}</span>
        </div>
      );
    }
    if (isWinning && !isSeller) {
      return (
        <div className="flex items-center gap-2 rounded-full bg-[#44a044] px-4 py-1.5 text-[12px] font-black uppercase tracking-wider text-white shadow-lg shadow-green-900/20 ring-2 ring-white/20 animate-in zoom-in-50 duration-300">
          <span className="h-2 w-2 rounded-full bg-white animate-pulse" />
          <span>{auction.status === "live" ? "Du leder" : "Vunnet"}</span>
        </div>
      );
    }
    if (isOutbid && !isSeller) {
      return (
        <div className="flex items-center gap-2 rounded-full bg-accent px-5 py-2 text-[14px] font-black uppercase tracking-widest text-white shadow-xl shadow-accent/40 ring-4 ring-white/30 animate-in zoom-in-75 duration-500">
          <X className="h-5 w-5 stroke-[4px]" />
          <span>OVERBUDT</span>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-1.5 rounded-full bg-white/90 backdrop-blur px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-primary shadow-sm">
        <span className={`h-1.5 w-1.5 rounded-full animate-pulse ${auction.status === "live" ? "bg-primary" : "bg-muted-foreground"}`} />
        <span>{auction.status === "live" ? "Aktiv" : "Avsluttet"}</span>
      </div>
    );
  })();

  const wrapperClass = [
    isWinning ? "winning-glow ring-1 ring-green-600/20" : "",
    isOutbid ? "outbid-glow ring-1 ring-accent/20" : "",
  ].filter(Boolean).join(" ");

  const wrapperStyle = (isWinning || isOutbid)
    ? { border: "1px solid var(--border)", cursor: "pointer" as const }
    : undefined;

  return (
    <AuctionCardWrapper
      onClick={() => navigate(`/${organizationSlug}/budrunder/${auction.id}`)}
      className={wrapperClass}
      style={wrapperStyle}
    >
      <AuctionCardImage
        image={auction.primaryImage}
        title={auction.title}
        topLeft={statusBadge}
        topRight={auction.type === "flash" ? <FlashBadge /> : undefined}
        bottomRight={
          !isSeller && onToggleFavorite ? (
            <FavoriteButton isFavorite={isFavorite} onToggle={() => onToggleFavorite(auction.id)} />
          ) : undefined
        }
      />

      <div className="flex flex-1 flex-col justify-between p-6">
        <AuctionCardHeader
          title={auction.title}
          details={details}
          bidCount={auction.bidCount}
          referenceNumber={auction.referenceNumber}
          registrationNumber={v?.registrationNumber}
          timingSlot={
            auction.status === "live" && auction.endsAt
              ? <LiveTimingBadge endsAt={auction.endsAt} />
              : <EndedTimingBadge closedAt={auction.closedAt} endsAt={auction.endsAt} createdAt={auction.createdAt} />
          }
        />

        {/* Price + Bid Buttons */}
        <div className="mt-6 flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between border-t border-border pt-4 gap-4">
            <div className="flex flex-col">
              <p className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">
                {auction.bidCount > 0 ? (auction.status === "live" ? "Gjeldende bud" : "Vinnende bud") : "Startpris"}
              </p>
              <p
                className={`text-3xl font-black transition-all ${highlightBid ? "animate-bid-pop text-green-600" : "text-primary"}`}
                style={{ fontFamily: "var(--font-display)" }}
              >
                {formatCurrency(currentDisplayedBid)}
              </p>
            </div>

            {!isSeller && auction.status === "live" && (
              <div className="flex w-full flex-col gap-2 sm:w-auto">
                <div className="flex flex-wrap gap-2">
                  {[auction.minimumIncrement, auction.minimumIncrement * 5, auction.minimumIncrement * 10].map((inc: number) => (
                    <button
                      key={inc}
                      onClick={(e) => { e.stopPropagation(); void placeBid(inc); }}
                      disabled={submitting}
                      className="flex h-10 flex-1 items-center justify-center rounded-xl border border-accent/20 bg-accent/5 px-4 text-xs font-bold text-accent transition-all hover:bg-accent hover:text-white disabled:opacity-50 sm:flex-none"
                    >
                      +{formatCurrency(inc)}
                    </button>
                  ))}
                </div>
                {bidError && <p className="text-right text-[10px] font-semibold text-destructive">{bidError}</p>}
              </div>
            )}
          </div>
        </div>
      </div>
    </AuctionCardWrapper>
  );
}
